import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { findFirstVideo, stripVideoUrlsFromText } from '../utils/videoProviders';
import { parseContentWithHashtagsAndUrls } from '../utils/hashtags';
import { getMediaUrl } from '../utils/mediaUrl';
import FeedVideo from './FeedVideo';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useSocket } from '../contexts/SocketContext';
import { useFeatures } from '../contexts/FeatureContext';
import { useMobile } from '../utils/useMobile';
import VideoEmbed from './VideoEmbed';
import LinkPreview from './LinkPreview';
import ShareSheet from './ShareSheet';
import CommentsSection from './CommentsSection';
import CommentsModal from './CommentsModal';
import NativeImageViewer from './NativeImageViewer';
import AudioPlayer from './AudioPlayer';
import TahaqqoqNoteCard from './TahaqqoqNoteCard';
import DirhamTipModal from './DirhamTipModal';
import { formatTimeAgo } from '../utils/timeAgo';
import { triggerHaptic, nativeShare, openExternalUrl } from '../utils/nativeBridge';
import { getPublicWebUrl } from '../config';

// "#t=0.1" + preload="metadata" makes the browser fetch only the start of the
// file and paint the first frame as a preview, instead of a black box.
const GridMedia = ({ item, alt }) => {
  if (item.type === 'video') {
    return (
      <FeedVideo url={item.url} fill />
    );
  }
  return (
    <img
      src={getMediaUrl(item.url)}
      alt={alt}
      className="w-full h-full object-cover hover:scale-102 transition-transform duration-200"
    />
  );
};

const PostCard = ({ post, onUpdate, showCommentsInline = true, isDetailView = false, onQuoteBazz = null }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useMobile();
  const toast = useToast();
  const socket = useSocket();
  const { user: currentUser } = useAuth();
  const { isFeatureEnabled } = useFeatures();
  const isDirhamEnabled = isFeatureEnabled('dirham');

  const [liked, setLiked] = useState(post.isLiked || false);
  const [likesCount, setLikesCount] = useState(post.likesCount || 0);
  const [reBazzed, setReBazzed] = useState(post.isReBazzed || false);
  const [reBazzesCount, setReBazzesCount] = useState(post.reBazzesCount || 0);
  const [bookmarked, setBookmarked] = useState(post.isBookmarked || false);
  const [poll, setPoll] = useState(post.poll || null);

  const [isPinned, setIsPinned] = useState(post.isPinned || false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translatedText, setTranslatedText] = useState(null);
  const [detectedSourceLang, setDetectedSourceLang] = useState(null);
  const [showTranslation, setShowTranslation] = useState(false);
  const [showTipModal, setShowTipModal] = useState(false);

  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isReporting, setIsReporting] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showReBazzMenu, setShowReBazzMenu] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [pollVoting, setPollVoting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [avatarError, setAvatarError] = useState(false);

  const [isHidden, setIsHidden] = useState(post.isHidden || false);
  const [isHiding, setIsHiding] = useState(false);

  useEffect(() => {
    setLiked(post.isLiked || false);
    setLikesCount(post.likesCount || 0);
    setReBazzed(post.isReBazzed || false);
    setReBazzesCount(post.reBazzesCount || 0);
    setBookmarked(post.isBookmarked || false);
    setPoll(post.poll || null);
    setIsPinned(post.isPinned || false);
    setIsHidden(post.isHidden || false);
  }, [post.id, post.isLiked, post.likesCount, post.isReBazzed, post.reBazzesCount, post.isBookmarked, post.poll, post.isPinned, post.isHidden]);

  // Real-time live poll updates via Socket.IO
  useEffect(() => {
    if (!socket || !post.id) return;
    const handlePollUpdate = (data) => {
      if (data && (data.postId === post.id || data.publicId === post.id || String(data.postId) === String(post.id))) {
        setPoll((prev) => {
          if (!prev) return data.poll;
          return {
            ...prev,
            ...data.poll,
            userVotedOptionId: prev.userVotedOptionId || data.poll?.userVotedOptionId
          };
        });
      }
    };
    socket.on('poll:update', handlePollUpdate);
    return () => {
      socket.off('poll:update', handlePollUpdate);
    };
  }, [socket, post.id]);

  const imageVideoMedia = (post.media || []).filter(
    (m) => m.type === 'image' || m.type === 'video'
  );
  const audioMedia = (post.media || []).filter((m) => m.type === 'audio');

  // Close menus on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.post-menu, .post-menu-sheet')) setShowMenu(false);
      // The share sheet closes itself (backdrop / Cancel)
      if (!e.target.closest('.post-share-menu, .share-sheet')) setShowShareMenu(false);
      if (!e.target.closest('.post-rebazz-menu, .post-rebazz-sheet')) setShowReBazzMenu(false);
    };
    if (showMenu || showShareMenu || showReBazzMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu, showShareMenu, showReBazzMenu]);



  const videoInfo = findFirstVideo(post.content);
  const formatContent = (content) => stripVideoUrlsFromText(content || '');

  // Optimistic Like
  const handleLike = async (e) => {
    e.stopPropagation();
    triggerHaptic('light');
    const nextLiked = !liked;
    const nextCount = nextLiked ? likesCount + 1 : Math.max(0, likesCount - 1);
    setLiked(nextLiked);
    setLikesCount(nextCount);

    try {
      await axios.post(`/api/posts/${post.id}/like`);
    } catch (error) {
      setLiked(!nextLiked);
      setLikesCount(likesCount);
      toast.error('Failed to update like');
    }
  };

  // Optimistic Re-bazz
  const handleReBazz = async (e) => {
    e.stopPropagation();
    triggerHaptic('medium');
    setShowReBazzMenu(false);
    const nextReBazzed = !reBazzed;
    const nextCount = nextReBazzed ? reBazzesCount + 1 : Math.max(0, reBazzesCount - 1);
    setReBazzed(nextReBazzed);
    setReBazzesCount(nextCount);

    try {
      await axios.post(`/api/posts/${post.id}/re-bazz`);
      toast.success(nextReBazzed ? 'Re-bazzed to your profile' : 'Removed re-bazz');
    } catch (error) {
      setReBazzed(!nextReBazzed);
      setReBazzesCount(reBazzesCount);
      toast.error(error.response?.data?.error || 'Failed to re-bazz');
    }
  };

  const handleQuoteBazz = (e) => {
    e.stopPropagation();
    setShowReBazzMenu(false);
    if (onQuoteBazz) onQuoteBazz(post);
  };

  // Optimistic Bookmark
  const handleBookmark = async (e) => {
    e.stopPropagation();
    triggerHaptic('selection');
    const nextBookmarked = !bookmarked;
    setBookmarked(nextBookmarked);

    try {
      if (bookmarked) {
        await axios.delete(`/api/posts/${post.id}/bookmark`);
        toast.info('Removed from bookmarks');
      } else {
        await axios.post(`/api/posts/${post.id}/bookmark`);
        toast.success('Saved to bookmarks');
      }
    } catch (error) {
      setBookmarked(!nextBookmarked);
      toast.error('Failed to update bookmark');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('post.deleteConfirm', 'Are you sure you want to delete this post?'))) {
      setShowMenu(false);
      return;
    }

    setIsDeleting(true);
    setShowMenu(false);
    try {
      await axios.delete(`/api/posts/${post.id}`);
      toast.success('Post deleted');
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.error || 'Failed to delete post');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleReport = async () => {
    setShowMenu(false);
    setIsReporting(true);
    try {
      await axios.post(`/api/posts/${post.id}/report`, { reason: null });
      toast.success('Thank you. Your report has been submitted.');
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Report error:', error);
      toast.error(error.response?.data?.error || 'Failed to report post');
    } finally {
      setIsReporting(false);
    }
  };

  const handleAdminToggleHide = async () => {
    setShowMenu(false);
    setIsHiding(true);
    try {
      const res = await axios.patch(`/api/admin/posts/${post.id}/hide`, {
        isHidden: !isHidden,
        hidden: !isHidden,
        reason: !isHidden ? 'Hidden by administrator' : null,
      });
      setIsHidden(res.data?.post?.is_hidden ?? !isHidden);
      toast.success(
        res.data?.message || (!isHidden ? 'Post set to author-only' : 'Post restored to public')
      );
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Admin hide error:', err);
      toast.error(err.response?.data?.error || 'Failed to update post visibility');
    } finally {
      setIsHiding(false);
    }
  };

  const handleAdminDelete = async () => {
    if (
      !window.confirm(
        'ADMIN ACTION: Permanently delete this post and all associated comments, media, and records? This cannot be undone.'
      )
    ) {
      setShowMenu(false);
      return;
    }

    setShowMenu(false);
    setIsDeleting(true);
    try {
      await axios.delete(`/api/admin/posts/${post.id}`);
      toast.success('Post permanently deleted by admin');
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Admin delete error:', err);
      toast.error(err.response?.data?.error || 'Failed to delete post');
    } finally {
      setIsDeleting(false);
    }
  };

  const startEdit = () => {
    setShowMenu(false);
    setEditContent(post.content || '');
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditContent('');
  };

  const saveEdit = async () => {
    const trimmed = editContent.trim();
    if (trimmed.length > 280) {
      toast.error('Content must be 280 characters or less.');
      return;
    }
    setSavingEdit(true);
    try {
      await axios.put(`/api/posts/${post.id}`, { content: trimmed || post.content || '' });
      toast.success('Post updated');
      if (onUpdate) onUpdate();
      setIsEditing(false);
      setEditContent('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update post');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleTogglePin = async () => {
    setShowMenu(false);
    try {
      const res = await axios.post(`/api/posts/${post.id}/pin`);
      setIsPinned(res.data.isPinned);
      toast.success(
        res.data.isPinned
          ? t('post.pinnedSuccess', 'Pinned to your profile')
          : t('post.unpinnedSuccess', 'Unpinned from your profile')
      );
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update pin');
    }
  };

  const handleToggleTranslate = async (e) => {
    e.stopPropagation();
    if (showTranslation) {
      setShowTranslation(false);
      return;
    }
    if (translatedText) {
      setShowTranslation(true);
      return;
    }
    setIsTranslating(true);
    try {
      const targetLang = i18n.language || 'en';
      const res = await axios.post(`/api/posts/${post.id}/translate`, { targetLanguage: targetLang });
      setTranslatedText(res.data?.translatedText);
      setDetectedSourceLang(res.data?.detectedSourceLanguage);
      setShowTranslation(true);
    } catch (err) {
      console.error('Translation error:', err);
      toast.error('Failed to translate post');
    } finally {
      setIsTranslating(false);
    }
  };

  const postShareId = post.publicId || post.public_id || post.id;
  const postUrl = getPublicWebUrl(`/post/${postShareId}`);
  // Cut by characters, not UTF-16 units: slicing through an emoji leaves half of it,
  // and encodeURIComponent (share links below) throws on that, crashing the feed
  const contentChars = Array.from(post.content || '');
  const shareText = contentChars.slice(0, 100).join('') + (contentChars.length > 100 ? '…' : '');

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(postUrl);
      setShowShareMenu(false);
      toast.success('Link copied to clipboard!');
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const handleShareClick = async (e) => {
    e.stopPropagation();
    triggerHaptic('light');

    // Phones: straight to our share sheet (recent chats, copy link, other apps)
    if (isMobile) {
      setShowShareMenu(true);
      return;
    }

    const didShare = await nativeShare({
      title: post.author?.username ? `Post by ${post.author.username}` : 'Post on Bazzef',
      text: shareText,
      url: postUrl,
      dialogTitle: 'Share Post',
    });

    if (!didShare) {
      setShowShareMenu((prev) => !prev);
    }
  };

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(postUrl)}&text=${encodeURIComponent(shareText)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(postUrl)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + postUrl)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(postUrl)}`,
  };

  const handleShareExternal = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer,width=600,height=400');
    setShowShareMenu(false);
  };

  const handlePollVote = async (pollOptionId) => {
    if (pollVoting || (poll && poll.userVotedOptionId)) return;
    setPollVoting(true);

    const previousPoll = poll;
    // Optimistic vote update
    if (poll && poll.options) {
      const updatedOptions = poll.options.map((opt) => ({
        ...opt,
        voteCount: opt.id === pollOptionId ? (opt.voteCount || 0) + 1 : (opt.voteCount || 0)
      }));
      setPoll({
        ...poll,
        options: updatedOptions,
        userVotedOptionId: pollOptionId
      });
    }

    try {
      const res = await axios.post(`/api/posts/${post.id}/poll/vote`, { pollOptionId });
      if (res.data?.poll) {
        setPoll(res.data.poll);
      }
      toast.success('Vote submitted');
    } catch (err) {
      setPoll(previousPoll);
      toast.error(err.response?.data?.error || 'Failed to vote');
    } finally {
      setPollVoting(false);
    }
  };

  // Pass the already-loaded post (with local like/bookmark state) so the detail
  // page can render instantly instead of waiting for the network.
  const openPostDetail = () => {
    const targetId = post.id || post.publicId;
    if (!targetId) return;
    navigate(`/post/${targetId}`, {
      state: {
        post: {
          ...post,
          isLiked: liked,
          likesCount,
          isBookmarked: bookmarked,
          isReBazzed: reBazzed,
          reBazzesCount,
        },
      },
    });
  };

  const handlePostClick = (e) => {
    if (e.target.closest('button, a, video, audio, img, iframe, input, select, textarea, [role="button"]')) {
      return;
    }
    openPostDetail();
  };

  const isOwnPost = currentUser?.id === post.userId;

  // Multi-image grid renderer
  const renderMediaGrid = () => {
    if (imageVideoMedia.length === 0) return null;
    // Media-only posts sit directly under the author row; give them room to breathe
    const mediaTop = post.content ? 'mt-2.5' : 'mt-3.5';

    if (imageVideoMedia.length === 1) {
      const item = imageVideoMedia[0];
      return (
        <div
          className={`${mediaTop} rounded-2xl overflow-hidden border border-zinc-200 dark:border-zinc-800 bg-zinc-100 dark:bg-zinc-900 cursor-pointer`}
          onClick={(e) => {
            e.stopPropagation();
            setLightboxIndex(0);
          }}
        >
          {item.type === 'video' ? (
            <FeedVideo url={item.url} />
          ) : (
            <img
              src={getMediaUrl(item.url)}
              alt="Media"
              className="w-full max-h-[460px] object-cover hover:opacity-95 transition-opacity"
            />
          )}
        </div>
      );
    }

    if (imageVideoMedia.length === 2) {
      return (
        <div className={`${mediaTop} grid grid-cols-2 gap-1.5 rounded-2xl overflow-hidden border border-zinc-200 dark:border-zinc-800`}>
          {imageVideoMedia.map((item, idx) => (
            <div
              key={idx}
              className="aspect-[4/3] bg-zinc-100 dark:bg-zinc-900 cursor-pointer overflow-hidden"
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex(idx);
              }}
            >
              <GridMedia
                item={item}
                alt={`Media ${idx + 1}`}
                />
            </div>
          ))}
        </div>
      );
    }

    if (imageVideoMedia.length === 3) {
      return (
        <div className={`${mediaTop} grid grid-cols-2 gap-1.5 rounded-2xl overflow-hidden border border-zinc-200 dark:border-zinc-800 aspect-[16/10]`}>
          <div
            className="h-full bg-zinc-100 dark:bg-zinc-900 cursor-pointer overflow-hidden"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxIndex(0);
            }}
          >
            <GridMedia
              item={imageVideoMedia[0]}
              alt="Media 1"
              />
          </div>
          <div className="grid grid-rows-2 gap-1.5 h-full">
            {imageVideoMedia.slice(1, 3).map((item, idx) => (
              <div
                key={idx}
                className="bg-zinc-100 dark:bg-zinc-900 cursor-pointer overflow-hidden"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex(idx + 1);
                }}
              >
                <GridMedia
                  item={item}
                  alt={`Media ${idx + 2}`}
                  />
              </div>
            ))}
          </div>
        </div>
      );
    }

    // 4 items: 2x2 grid
    return (
      <div className={`${mediaTop} grid grid-cols-2 gap-1.5 rounded-2xl overflow-hidden border border-zinc-200 dark:border-zinc-800`}>
        {imageVideoMedia.slice(0, 4).map((item, idx) => (
          <div
            key={idx}
            className="aspect-[4/3] bg-zinc-100 dark:bg-zinc-900 cursor-pointer overflow-hidden"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxIndex(idx);
            }}
          >
            <GridMedia
              item={item}
              alt={`Media ${idx + 1}`}
              />
          </div>
        ))}
      </div>
    );
  };

  const authorHandle = (
    post.author?.handle ||
    post.author_handle ||
    post.handle ||
    post.author?.username ||
    post.username ||
    ''
  ).replace(/^@+/, '').trim();
  const authorUsername = post.author?.username || post.username || authorHandle || 'User';
  const authorProfilePicture = post.author?.profilePicture || post.profile_picture;
  const authorIsVerified = post.author?.isVerified || post.is_verified;

  const handleAvatarClick = (e) => {
    e.stopPropagation();
    if (authorHandle) {
      navigate(`/profile/${authorHandle}`);
    }
  };

  const postAgeMinutes = (Date.now() - new Date(post.createdAt).getTime()) / (60 * 1000);
  const canEdit = isOwnPost && postAgeMinutes <= 15;

  const renderOptionsMenu = () => (
    <div className="relative post-menu">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          triggerHaptic('light');
          setShowMenu((prev) => !prev);
        }}
        className="w-8 h-8 rounded-full flex items-center justify-center text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors active:scale-90"
        title="Options"
      >
        <i className="fas fa-ellipsis-h text-base" />
      </button>

      {/* Desktop Dropdown */}
      {showMenu && !isMobile && (
        <div
          className="absolute right-0 top-9 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl shadow-xl py-1.5 z-20 min-w-[170px]"
          onClick={(e) => e.stopPropagation()}
        >
          {isOwnPost ? (
            <>
              {canEdit && (
                <button
                  type="button"
                  onClick={startEdit}
                  className="w-full text-left px-4 py-2.5 text-xs sm:text-sm font-semibold text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 flex items-center gap-2.5 transition-colors"
                >
                  <i className="fas fa-edit text-sm" />
                  <span>{t('common.edit', 'Edit')}</span>
                </button>
              )}
              {!post.threadId && (
                <button
                  type="button"
                  onClick={handleTogglePin}
                  className="w-full text-left px-4 py-2.5 text-xs sm:text-sm font-semibold text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 flex items-center gap-2.5 transition-colors"
                >
                  <i className="fas fa-thumbtack text-sm rotate-45 text-sky-500" />
                  <span>
                    {isPinned
                      ? t('post.unpin', 'Unpin from profile')
                      : t('post.pin', 'Pin to your profile')}
                  </span>
                </button>
              )}
              <button
                type="button"
                onClick={handleDelete}
                disabled={isDeleting}
                className="w-full text-left px-4 py-2.5 text-xs sm:text-sm font-semibold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 flex items-center gap-2.5 transition-colors disabled:opacity-50"
              >
                <i className="fas fa-trash text-sm" />
                <span>{isDeleting ? t('common.deleting', 'Deleting...') : t('common.delete', 'Delete')}</span>
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={handleReport}
              disabled={isReporting}
              className="w-full text-left px-4 py-2.5 text-xs sm:text-sm font-semibold text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 flex items-center gap-2.5 transition-colors disabled:opacity-50"
            >
              <i className="fas fa-flag text-sm" />
              <span>{isReporting ? 'Reporting...' : 'Report post'}</span>
            </button>
          )}

          {/* Admin Moderation Actions */}
          {currentUser?.isAdmin && (
            <div className="border-t border-zinc-100 dark:border-zinc-800 my-1 pt-1">
              <div className="px-3.5 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-500">
                Admin Moderation
              </div>
              <button
                type="button"
                onClick={handleAdminToggleHide}
                disabled={isHiding}
                className="w-full text-left px-4 py-2 text-xs sm:text-sm font-semibold text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/30 flex items-center gap-2.5 transition-colors disabled:opacity-50"
              >
                <i className={`fas ${isHidden ? 'fa-eye' : 'fa-eye-slash'} text-sm w-4 text-center`} />
                <span>{isHidden ? 'Make Public' : 'Make Invisible (Author Only)'}</span>
              </button>
              {!isOwnPost && (
                <button
                  type="button"
                  onClick={handleAdminDelete}
                  disabled={isDeleting}
                  className="w-full text-left px-4 py-2 text-xs sm:text-sm font-semibold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 flex items-center gap-2.5 transition-colors disabled:opacity-50"
                >
                  <i className="fas fa-trash text-sm w-4 text-center" />
                  <span>{isDeleting ? 'Deleting...' : 'Admin: Delete Post'}</span>
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Mobile Native Action Sheet Bottom Sheet (Portaled to document.body so parent transforms/filters don't trap or push it offscreen) */}
      {showMenu && isMobile && createPortal(
        <div
          className="post-menu-sheet fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-xs p-0 animate-in fade-in duration-200 select-none"
          onClick={(e) => {
            e.stopPropagation();
            setShowMenu(false);
          }}
          onPointerDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
        >
          <div
            className="w-full max-w-lg bg-white dark:bg-zinc-950 rounded-t-3xl border-t border-zinc-200 dark:border-zinc-800 p-4 pb-[calc(1.25rem+env(safe-area-inset-bottom))] shadow-2xl flex flex-col gap-1.5 animate-in slide-in-from-bottom duration-200 select-none"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Grab handle */}
            <div className="w-10 h-1.5 bg-zinc-300 dark:bg-zinc-700 rounded-full mx-auto mb-2" />

            {isOwnPost ? (
              <>
                {canEdit && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMenu(false);
                      startEdit();
                    }}
                    className="w-full text-left px-4 py-3.5 rounded-2xl text-[15px] font-semibold text-zinc-800 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-900 active:bg-zinc-100 dark:active:bg-zinc-800 flex items-center gap-3 transition-colors touch-press"
                  >
                    <i className="fas fa-edit text-base text-sky-500 w-5 text-center" />
                    <span>{t('common.edit', 'Edit')}</span>
                  </button>
                )}
                {!post.threadId && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMenu(false);
                      handleTogglePin();
                    }}
                    className="w-full text-left px-4 py-3.5 rounded-2xl text-[15px] font-semibold text-zinc-800 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-900 active:bg-zinc-100 dark:active:bg-zinc-800 flex items-center gap-3 transition-colors touch-press"
                  >
                    <i className="fas fa-thumbtack text-base rotate-45 text-sky-500 w-5 text-center" />
                    <span>
                      {isPinned
                        ? t('post.unpin', 'Unpin from profile')
                        : t('post.pin', 'Pin to your profile')}
                    </span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete();
                  }}
                  disabled={isDeleting}
                  className="w-full text-left px-4 py-3.5 rounded-2xl text-[15px] font-semibold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 active:bg-rose-100 dark:active:bg-rose-900/40 flex items-center gap-3 transition-colors disabled:opacity-50 touch-press"
                >
                  <i className="fas fa-trash text-base w-5 text-center" />
                  <span>{isDeleting ? t('common.deleting', 'Deleting...') : t('common.delete', 'Delete Post')}</span>
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleReport();
                }}
                disabled={isReporting}
                className="w-full text-left px-4 py-3.5 rounded-2xl text-[15px] font-semibold text-zinc-800 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-900 active:bg-zinc-100 dark:active:bg-zinc-800 flex items-center gap-3 transition-colors disabled:opacity-50 touch-press"
              >
                <i className="fas fa-flag text-base text-amber-500 w-5 text-center" />
                <span>{isReporting ? 'Reporting...' : 'Report Post'}</span>
              </button>
            )}

            {/* Admin Moderation Actions */}
            {currentUser?.isAdmin && (
              <div className="border-t border-zinc-100 dark:border-zinc-800 my-1 pt-1.5 flex flex-col gap-1">
                <div className="px-4 py-1 text-[11px] font-bold uppercase tracking-wider text-amber-500">
                  Admin Moderation
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAdminToggleHide();
                  }}
                  disabled={isHiding}
                  className="w-full text-left px-4 py-3.5 rounded-2xl text-[15px] font-semibold text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/30 active:bg-amber-100 flex items-center gap-3 transition-colors disabled:opacity-50 touch-press"
                >
                  <i className={`fas ${isHidden ? 'fa-eye' : 'fa-eye-slash'} text-base w-5 text-center`} />
                  <span>{isHidden ? 'Make Public' : 'Make Invisible (Author Only)'}</span>
                </button>
                {!isOwnPost && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAdminDelete();
                    }}
                    disabled={isDeleting}
                    className="w-full text-left px-4 py-3.5 rounded-2xl text-[15px] font-semibold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 active:bg-rose-100 flex items-center gap-3 transition-colors disabled:opacity-50 touch-press"
                  >
                    <i className="fas fa-trash text-base w-5 text-center" />
                    <span>{isDeleting ? 'Deleting...' : 'Admin: Delete Post'}</span>
                  </button>
                )}
              </div>
            )}

            {/* Cancel Button */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(false);
              }}
              className="w-full mt-2 py-3.5 rounded-2xl bg-zinc-100 dark:bg-zinc-800/80 text-zinc-900 dark:text-zinc-100 font-bold text-center active:scale-98 transition-transform text-[15px] touch-press"
            >
              {t('common.cancel', 'Cancel')}
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );

  const renderPostContent = () => {
    if (isEditing) {
      return (
        <div className="mb-2" onClick={(e) => e.stopPropagation()}>
          <textarea
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            rows={3}
            maxLength={280}
            className="w-full px-3.5 py-2.5 border border-zinc-200 dark:border-zinc-700 rounded-xl bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 text-base focus:outline-none focus:ring-2 focus:ring-sky-500"
            autoFocus
          />
          <div className="flex justify-end gap-2 mt-2">
            <button
              type="button"
              onClick={cancelEdit}
              className="px-3.5 py-1.5 text-xs sm:text-sm font-semibold text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveEdit}
              disabled={savingEdit}
              className="px-4 py-1.5 text-xs sm:text-sm font-bold text-white bg-zinc-900 dark:bg-white dark:text-zinc-900 rounded-full"
            >
              {savingEdit ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      );
    }

    if (!post.content) return null;

    const displayContent = showTranslation && translatedText ? translatedText : post.content;

    return (
      <div className="w-full">
        <p
          dir="auto"
          className={`w-full text-zinc-900 dark:text-zinc-100 leading-[1.65] break-words whitespace-pre-wrap font-normal select-text ${
            isDetailView
              ? 'text-[18.5px] sm:text-[20px] my-3.5'
              : 'text-[16.5px] sm:text-[17px] mb-2'
          }`}
        >
          {parseContentWithHashtagsAndUrls(formatContent(displayContent)).map((seg, i) =>
            seg.type === 'hashtag' ? (
              <Link
                key={i}
                to={`/hashtag/${encodeURIComponent(seg.value)}`}
                onClick={(e) => e.stopPropagation()}
                className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium inline-block"
                dir="ltr"
              >
                <bdi dir="ltr">#{seg.value}</bdi>
              </Link>
            ) : seg.type === 'mention' ? (
              <Link
                key={i}
                to={`/profile/${encodeURIComponent(seg.value)}`}
                onClick={(e) => e.stopPropagation()}
                className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium inline-block"
                dir="ltr"
              >
                <bdi dir="ltr">@{seg.value}</bdi>
              </Link>
            ) : seg.type === 'url' ? (
              <a
                key={i}
                href={seg.href || seg.value}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openExternalUrl(seg.href || seg.value);
                }}
                className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium break-all"
              >
                {seg.value}
              </a>
            ) : (
              <span key={i}>{seg.value}</span>
            )
          )}
        </p>

        {/* Translation Banner / Action */}
        {showTranslation && translatedText ? (
          <div
            className="flex items-center gap-1.5 text-[12px] text-zinc-500 dark:text-zinc-400 mb-2 py-0.5"
            onClick={(e) => e.stopPropagation()}
          >
            <i className="fas fa-language text-indigo-500 text-xs" />
            <span>
              {t('post.translatedFrom', 'Translated from {{lang}}', {
                lang: detectedSourceLang ? detectedSourceLang.toUpperCase() : 'detected language',
              })}
            </span>
            <span>·</span>
            <button
              type="button"
              onClick={handleToggleTranslate}
              className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
            >
              {t('post.showOriginal', 'Show original')}
            </button>
          </div>
        ) : (
          <div className="mb-2" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={handleToggleTranslate}
              disabled={isTranslating}
              className="text-[12px] font-medium text-slate-400 hover:text-zinc-600 dark:hover:text-zinc-300 inline-flex items-center gap-1.5 transition-colors"
            >
              <i className={`fas fa-language text-xs ${isTranslating ? 'animate-pulse text-indigo-500' : ''}`} />
              <span>{isTranslating ? t('post.translating', 'Translating...') : t('post.translate', 'Translate post')}</span>
            </button>
          </div>
        )}
      </div>
    );
  };

  const getPollTimeRemaining = (endsAt) => {
    if (!endsAt) return null;
    const diff = new Date(endsAt).getTime() - Date.now();
    if (diff <= 0) return 'Final results';
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours >= 48) {
      const days = Math.floor(hours / 24);
      return `${days} days left`;
    }
    if (hours >= 1) {
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} left`;
    }
    const minutes = Math.max(1, Math.floor(diff / (1000 * 60)));
    return `${minutes} ${minutes === 1 ? 'min' : 'mins'} left`;
  };

  const renderPoll = () => {
    if (!poll || !poll.options?.length) return null;

    const totalVotes = poll.options.reduce((s, o) => s + (o.voteCount || 0), 0);
    const endsAt = post.pollEndsAt || poll.endsAt;
    const ended = endsAt && new Date(endsAt) <= new Date();
    const userVoted = !!poll.userVotedOptionId;
    const showResults = userVoted || ended;
    const maxVotes = Math.max(...poll.options.map((o) => o.voteCount || 0), 1);

    return (
      <div
        className="w-full mt-3 mb-2 flex flex-col gap-2 select-none"
        onClick={(e) => e.stopPropagation()}
      >
        {poll.options.map((opt, idx) => {
          const pct = totalVotes > 0 ? Math.round(((opt.voteCount || 0) / totalVotes) * 100) : 0;
          const isChosen = poll.userVotedOptionId === opt.id;
          const isLeading = showResults && opt.voteCount === maxVotes && opt.voteCount > 0;

          return (
            <button
              key={opt.id || idx}
              type="button"
              disabled={showResults || pollVoting}
              onClick={() => handlePollVote(opt.id)}
              className={`relative w-full h-11 rounded-xl border overflow-hidden flex items-center justify-between px-3.5 text-left rtl:text-right transition-all duration-150 ${
                showResults
                  ? isChosen || isLeading
                    ? 'border-indigo-500/40 bg-white dark:bg-zinc-900'
                    : 'border-slate-200/80 dark:border-zinc-800 bg-white dark:bg-zinc-900'
                  : 'border-slate-200/90 dark:border-zinc-800 bg-slate-50/40 dark:bg-zinc-900/40 active:scale-[0.99] active:border-indigo-400 active:bg-indigo-50/40 dark:active:bg-indigo-950/40 cursor-pointer'
              }`}
            >
              {/* Animated Percentage Fill Bar (Only visible after voting or when ended) */}
              {showResults && (
                <div
                  style={{ width: `${pct}%` }}
                  className={`absolute inset-y-0 ltr:left-0 rtl:right-0 transition-all duration-500 ease-out ${
                    isChosen || isLeading
                      ? 'bg-indigo-500/15 dark:bg-indigo-500/25'
                      : 'bg-slate-200/60 dark:bg-zinc-800/80'
                  }`}
                />
              )}

              {/* Option Label + Selected Checkmark Badge */}
              <div className="relative z-10 flex items-center gap-2 min-w-0 pr-2 rtl:pr-0 rtl:pl-2">
                <span
                  dir="auto"
                  className={`text-[15px] leading-[20px] truncate ${
                    showResults && (isChosen || isLeading)
                      ? 'font-semibold text-slate-900 dark:text-zinc-100'
                      : showResults
                      ? 'font-medium text-slate-700 dark:text-zinc-300'
                      : 'font-medium text-slate-800 dark:text-zinc-200'
                  }`}
                >
                  {opt.optionText}
                </span>
                {isChosen && (
                  <span className="w-4 h-4 rounded-full bg-indigo-600 text-white text-[10px] flex items-center justify-center shrink-0">
                    ✓
                  </span>
                )}
              </div>

              {/* Right Side: Percentage (Voted) or Crisp Subtle Target Ring (Unvoted) */}
              <div className="relative z-10 shrink-0">
                {showResults ? (
                  <span
                    className={`text-[14px] tabular-nums ${
                      isChosen || isLeading
                        ? 'font-semibold text-indigo-600 dark:text-indigo-400'
                        : 'font-medium text-slate-500 dark:text-zinc-400'
                    }`}
                  >
                    {pct}%
                  </span>
                ) : (
                  <span className="w-4 h-4 rounded-full border-[1.5px] border-slate-300 dark:border-zinc-600 block transition-colors group-active:border-indigo-500" />
                )}
              </div>
            </button>
          );
        })}

        {/* Flush Footer Metadata: Vote Count + Expiration Countdown */}
        <div className="mt-0.5 flex items-center justify-between text-[13px] text-slate-500 dark:text-zinc-400 px-0.5">
          <span>
            {totalVotes.toLocaleString()} {totalVotes === 1 ? t('poll.vote', 'vote') : t('poll.votes', 'votes')} · {getPollTimeRemaining(endsAt)}
          </span>
          {userVoted && (
            <span className="text-[12px] font-semibold text-indigo-600 dark:text-indigo-400">
              {t('poll.voted', 'Voted')}
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderQuotedPost = () => {
    if (!post.quotedPost) return null;
    const qp = post.quotedPost;
    const qHandle = (qp.author?.handle || qp.author?.username || '').replace(/^@+/, '');
    const qUsername = qp.author?.username || qHandle || 'User';
    const qPic = qp.author?.profilePicture;
    const qMedia = (qp.media || []).find((m) => m.type === 'image');
    const qExcerpt = qp.content ? qp.content.slice(0, 200) + (qp.content.length > 200 ? '…' : '') : '';

    return (
      <div
        className="mt-2.5 mb-1 border border-zinc-200 dark:border-zinc-800 rounded-2xl p-3 cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-900/60 transition-colors"
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/post/${qp.id}`);
        }}
      >
        <div className="flex items-center gap-2 mb-1.5 min-w-0 flex-wrap">
          {qPic ? (
            <img
              src={getMediaUrl(qPic)}
              alt={qUsername}
              className="w-5 h-5 rounded-full object-cover border border-zinc-200 dark:border-zinc-700 shrink-0"
            />
          ) : (
            <div className="w-5 h-5 rounded-full bg-zinc-200 dark:bg-zinc-800 flex items-center justify-center text-[10px] font-bold text-zinc-600 dark:text-zinc-300 shrink-0">
              {qUsername.charAt(0).toUpperCase()}
            </div>
          )}
          <span className="font-bold text-zinc-900 dark:text-zinc-100 text-[13.5px] truncate">
            <bdi>{qUsername}</bdi>
          </span>
          {qHandle && (
            <span className="text-zinc-500 dark:text-zinc-400 text-xs truncate">
              <bdi dir="ltr">@{qHandle}</bdi>
            </span>
          )}
        </div>
        {qExcerpt && (
          <p dir="auto" className="w-full text-zinc-700 dark:text-zinc-300 text-[14px] leading-[1.6] break-words whitespace-pre-wrap mb-1.5">
            {qExcerpt}
          </p>
        )}
        {qMedia && (
          <img
            src={getMediaUrl(qMedia.url)}
            alt="Quoted media"
            className="w-full max-h-48 object-cover rounded-xl border border-zinc-200 dark:border-zinc-800"
          />
        )}
      </div>
    );
  };

  const renderActionRow = () => (
    <div
      className={`flex items-center justify-between text-zinc-500 dark:text-zinc-400 ${
        isDetailView
          ? 'max-w-lg'
          : 'mt-3 pt-1 max-w-md'
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Reply Button */}
      <button
        type="button"
        onClick={() => {
          if (isMobile && showCommentsInline) {
            openPostDetail();
          } else if (!isMobile) {
            setShowCommentsModal(true);
          } else {
            setShowComments(!showComments);
          }
        }}
        className="flex items-center gap-2 hover:text-sky-500 transition-colors group p-1 -m-1"
        aria-label="Reply"
      >
        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center group-hover:bg-sky-50 dark:group-hover:bg-sky-950/40 transition-colors">
          <i className="far fa-comment text-[18px] sm:text-[19px]" />
        </div>
        {(post.repliesCount || 0) > 0 && (
          <span className="text-[13.5px] sm:text-sm font-medium">{post.repliesCount}</span>
        )}
      </button>

      {/* Repost Button with Dropdown */}
      <div className="relative post-rebazz-menu">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setShowReBazzMenu((prev) => !prev);
          }}
          className={`flex items-center gap-2 hover:text-emerald-500 transition-colors group p-1 -m-1 ${
            reBazzed ? 'text-emerald-500 font-bold' : ''
          }`}
          aria-label="Re-bazz"
        >
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center group-hover:bg-emerald-50 dark:group-hover:bg-emerald-950/40 transition-colors">
            <i className="fas fa-retweet text-[18px] sm:text-[19px]" />
          </div>
          {(reBazzesCount || 0) > 0 && (
            <span className="text-[13.5px] sm:text-sm font-medium text-zinc-500 dark:text-zinc-400">{reBazzesCount}</span>
          )}
        </button>

        {/* Repost Menu: Desktop Dropdown */}
        {showReBazzMenu && !isMobile && (
          <div
            className="absolute left-0 bottom-full mb-2 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl shadow-xl py-1.5 z-30 min-w-[190px]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={handleReBazz}
              className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left text-xs sm:text-sm font-semibold text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
            >
              <i className="fas fa-retweet w-4 text-center text-emerald-500" />
              <span>{reBazzed ? t('quote.undoReBazz', 'Undo Re-bazz') : t('quote.reBazz', 'Re-bazz')}</span>
            </button>
            <button
              type="button"
              onClick={handleQuoteBazz}
              className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left text-xs sm:text-sm font-semibold text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
            >
              <i className="fas fa-pen w-4 text-center text-sky-500" />
              <span>{t('quote.quoteBazz', 'Quote Bazz')}</span>
            </button>
          </div>
        )}

        {/* Repost Menu: Mobile Action Bottom Sheet */}
        {showReBazzMenu && isMobile && createPortal(
          <div
            className="post-rebazz-sheet fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-xs p-0 animate-in fade-in duration-200 select-none"
            onClick={(e) => {
              e.stopPropagation();
              setShowReBazzMenu(false);
            }}
            onPointerDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
          >
            <div
              className="w-full max-w-lg bg-white dark:bg-zinc-950 rounded-t-3xl border-t border-zinc-200 dark:border-zinc-800 p-4 pb-[calc(1.25rem+env(safe-area-inset-bottom))] shadow-2xl flex flex-col gap-1.5 animate-in slide-in-from-bottom duration-200 select-none"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-10 h-1.5 bg-zinc-300 dark:bg-zinc-700 rounded-full mx-auto mb-2" />
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowReBazzMenu(false);
                  handleReBazz();
                }}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl text-[15px] font-semibold text-zinc-800 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-900 active:bg-zinc-100 dark:active:bg-zinc-800 transition-colors touch-press"
              >
                <i className="fas fa-retweet text-lg text-emerald-500 w-5 text-center" />
                <span>{reBazzed ? t('quote.undoReBazz', 'Undo Re-bazz') : t('quote.reBazz', 'Re-bazz')}</span>
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowReBazzMenu(false);
                  handleQuoteBazz();
                }}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl text-[15px] font-semibold text-zinc-800 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-900 active:bg-zinc-100 dark:active:bg-zinc-800 transition-colors touch-press"
              >
                <i className="fas fa-pen text-lg text-sky-500 w-5 text-center" />
                <span>{t('quote.quoteBazz', 'Quote Bazz')}</span>
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowReBazzMenu(false);
                }}
                className="w-full mt-2 py-3.5 rounded-2xl bg-zinc-100 dark:bg-zinc-800/80 text-zinc-900 dark:text-zinc-100 font-bold text-center active:scale-98 transition-transform text-[15px] touch-press"
              >
                {t('common.cancel', 'Cancel')}
              </button>
            </div>
          </div>,
          document.body
        )}
      </div>

      {/* Like Button with Pop animation */}
      <button
        type="button"
        onClick={handleLike}
        className={`flex items-center gap-2 hover:text-rose-500 transition-colors group p-1 -m-1 active:scale-90 transition-transform ${
          liked ? 'text-rose-500 font-bold' : ''
        }`}
        aria-label="Like"
      >
        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center group-hover:bg-rose-50 dark:group-hover:bg-rose-950/40 transition-colors">
          <i
            className={`text-[19px] sm:text-[20px] transition-transform active:scale-125 ${
              liked ? 'fas fa-heart text-rose-500 scale-105' : 'far fa-heart'
            }`}
          />
        </div>
        {(likesCount || 0) > 0 && (
          <span className="text-[13.5px] sm:text-sm font-medium text-zinc-500 dark:text-zinc-400">
            {likesCount}
          </span>
        )}
      </button>

      {/* Bookmark Button */}
      <button
        type="button"
        onClick={handleBookmark}
        className={`flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full hover:text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 transition-colors active:scale-90 transition-transform ${
          bookmarked ? 'text-indigo-500 font-bold' : ''
        }`}
        aria-label="Bookmark"
      >
        <i className={`text-[18px] sm:text-[19px] ${bookmarked ? 'fas fa-bookmark text-indigo-500' : 'far fa-bookmark'}`} />
      </button>

      {/* Dirham Dialna Tip Button (gated by feature toggle) */}
      {isDirhamEnabled && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (!currentUser) {
              toast.show(t('auth.loginRequired', 'Please sign in to send a tip'), 'info');
              return;
            }
            setShowTipModal(true);
          }}
          className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full hover:text-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 text-zinc-500 dark:text-zinc-400 transition-colors active:scale-90 transition-transform"
          title={t('dirham.tipTooltip', 'Tip Creator (Dirham Dialna)')}
          aria-label={t('dirham.tipTooltip', 'Tip Creator (Dirham Dialna)')}
        >
          <span className="text-[11px] font-black tracking-tighter text-emerald-600 dark:text-emerald-400 border border-emerald-500/40 px-1 py-0.5 rounded-md hover:bg-emerald-500 hover:text-white transition-colors">
            د.م
          </span>
        </button>
      )}

      {/* Share Menu Button */}
      <div className="relative post-share-menu">
        <button
          type="button"
          onClick={handleShareClick}
          className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full hover:text-sky-500 hover:bg-sky-50 dark:hover:bg-sky-950/40 transition-colors active:scale-90 transition-transform"
          aria-label="Share"
        >
          <i className="fas fa-arrow-up-from-bracket text-[18px] sm:text-[19px]" />
        </button>

        {/* Share Menu: Desktop Dropdown */}
        {showShareMenu && !isMobile && (
          <div
            className="absolute right-0 bottom-full mb-2 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl shadow-xl py-1.5 z-30 min-w-[200px]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={handleCopyLink}
              className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left text-xs sm:text-sm font-semibold text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
            >
              <i className="fas fa-link w-4 text-center text-zinc-400 text-sm" />
              <span>Copy link</span>
            </button>
            <button
              type="button"
              onClick={() => handleShareExternal(shareLinks.twitter)}
              className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left text-xs sm:text-sm font-semibold text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
            >
              <i className="fab fa-x-twitter w-4 text-center text-sm" />
              <span>Share on X</span>
            </button>
            <button
              type="button"
              onClick={() => handleShareExternal(shareLinks.whatsapp)}
              className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left text-xs sm:text-sm font-semibold text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
            >
              <i className="fab fa-whatsapp w-4 text-center text-emerald-500 text-sm" />
              <span>Share on WhatsApp</span>
            </button>
          </div>
        )}

        {/* Share sheet (phones): recent chats, copy link, other apps */}
        <ShareSheet
          open={showShareMenu && isMobile}
          onClose={() => setShowShareMenu(false)}
          url={postUrl}
          text={shareText}
          title={post.author?.username ? `Post by ${post.author.username}` : 'Post on Bazzef'}
        />
      </div>
    </div>
  );

  return (
    <div
      onClick={handlePostClick}
      className={`bg-white dark:bg-black border-b border-zinc-100 dark:border-zinc-800/80 px-4 transition-colors ${
        isDetailView
          ? 'py-4'
          : 'py-3.5 hover:bg-zinc-50/50 dark:hover:bg-zinc-950/40 active:bg-zinc-100/70 dark:active:bg-zinc-900/70 cursor-pointer'
      }`}
    >
      {/* Detail View Layout */}
      {isDetailView ? (
        <div>
          {isPinned && (
            <div className="flex items-center gap-1.5 text-xs font-semibold text-zinc-500 dark:text-zinc-400 mb-2.5">
              <i className="fas fa-thumbtack text-sky-500 rotate-45 text-[11px]" />
              <span>{t('post.pinnedPost', 'Pinned Post')}</span>
            </div>
          )}
          {/* Header Row: Avatar, Names, Options */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3 min-w-0">
              {authorHandle ? (
                <Link
                  to={`/profile/${authorHandle}`}
                  onClick={handleAvatarClick}
                  className="flex-shrink-0"
                  aria-label={`View ${authorUsername}'s profile`}
                >
                  {authorProfilePicture && !avatarError ? (
                    <img
                      src={getMediaUrl(authorProfilePicture)}
                      alt={authorUsername}
                      onError={() => setAvatarError(true)}
                      className="w-12 h-12 rounded-full object-cover border border-zinc-200 dark:border-zinc-800 hover:opacity-90 transition-opacity"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-zinc-200 dark:bg-zinc-800 flex items-center justify-center font-bold text-zinc-600 dark:text-zinc-300 text-base">
                      {authorUsername.charAt(0).toUpperCase()}
                    </div>
                  )}
                </Link>
              ) : (
                <div className="flex-shrink-0">
                  {authorProfilePicture && !avatarError ? (
                    <img
                      src={getMediaUrl(authorProfilePicture)}
                      alt={authorUsername}
                      onError={() => setAvatarError(true)}
                      className="w-12 h-12 rounded-full object-cover border border-zinc-200 dark:border-zinc-800"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-zinc-200 dark:bg-zinc-800 flex items-center justify-center font-bold text-zinc-600 dark:text-zinc-300 text-base">
                      {authorUsername.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
              )}

              <div className="min-w-0 flex flex-col">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {authorHandle ? (
                    <Link
                      to={`/profile/${authorHandle}`}
                      onClick={handleAvatarClick}
                      className="font-bold text-zinc-900 dark:text-zinc-100 hover:underline text-[17px] truncate"
                    >
                      <bdi>{authorUsername}</bdi>
                    </Link>
                  ) : (
                    <span className="font-bold text-zinc-900 dark:text-zinc-100 text-[17px] truncate">
                      <bdi>{authorUsername}</bdi>
                    </span>
                  )}
                  {authorIsVerified && (
                    <i className="fas fa-check-circle text-sky-500 text-sm" title="Verified" />
                  )}
                  {post.author?.accountType === 'organization' && (
                    <span className="text-zinc-400 text-xs" title="Organization">
                      <i className="fas fa-building" />
                    </span>
                  )}
                  {isHidden && (
                    <span
                      className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 inline-flex items-center gap-1 shrink-0"
                      title={currentUser?.isAdmin ? 'Hidden from public (visible only to author)' : 'This post is visible only to you'}
                    >
                      <i className="fas fa-eye-slash text-[10px]" />
                      {currentUser?.isAdmin ? 'Author Only (Hidden)' : 'Only visible to you'}
                    </span>
                  )}
                </div>
                {authorHandle && (
                  <Link
                    to={`/profile/${authorHandle}`}
                    onClick={handleAvatarClick}
                    className="text-zinc-500 dark:text-zinc-400 text-[14.5px] font-normal truncate hover:underline"
                  >
                    <bdi dir="ltr">@{authorHandle}</bdi>
                  </Link>
                )}
              </div>
            </div>

            {/* Post Options Menu */}
            {renderOptionsMenu()}
          </div>

          {/* Edit Box or Post Content */}
          {renderPostContent()}

          {/* Video Embed */}
          {videoInfo && <VideoEmbed provider={videoInfo.provider} videoId={videoInfo.videoId} />}

          {/* Link Preview */}
          {post.linkPreview && !videoInfo && (
            <div className="mb-2.5" onClick={(e) => e.stopPropagation()}>
              <LinkPreview preview={post.linkPreview} />
            </div>
          )}

          {/* Media Grid */}
          {renderMediaGrid()}

          {/* Voice Notes Audio Players */}
          {audioMedia.map((item, idx) => (
            <AudioPlayer key={idx} src={item.url} />
          ))}

          {/* Poll Component */}
          {renderPoll()}

          {/* Quoted Post Preview */}
          {renderQuotedPost()}

          {/* Tahaqqoq Community Context / Fact-Checking Note */}
          <TahaqqoqNoteCard postId={post.id} isPostAuthor={currentUser?.id === (post.author?.id || post.userId || post.user_id)} />

          {/* Detail View Full Timestamp (Drops redundant relative time) */}
          <div className="mt-3 text-[13px] text-zinc-500 dark:text-zinc-400 flex items-center gap-1.5 flex-wrap">
            <span>
              {new Date(post.createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} · {new Date(post.createdAt).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
            {post.editedAt && (
              <>
                <span>·</span>
                <span className="italic text-zinc-400 dark:text-zinc-500">(edited)</span>
              </>
            )}
          </div>

          {/* Unified Action Row (Redundant stats row removed, zero counts hidden) */}
          <div className="py-3 my-3 border-y border-zinc-100 dark:border-zinc-800/80">
            {renderActionRow()}
          </div>
        </div>
      ) : (
        /* Regular Feed Layout */
        <div>
          {isPinned && (
            <div className="flex items-center gap-1.5 text-xs font-semibold text-zinc-500 dark:text-zinc-400 mb-1.5 ms-14">
              <i className="fas fa-thumbtack text-sky-500 rotate-45 text-[11px]" />
              <span>{t('post.pinnedPost', 'Pinned Post')}</span>
            </div>
          )}
          <div className="flex gap-3">
          {/* Author Avatar */}
          {authorHandle ? (
            <Link
              to={`/profile/${authorHandle}`}
              onClick={handleAvatarClick}
              className="flex-shrink-0"
              aria-label={`View ${authorUsername}'s profile`}
            >
              {authorProfilePicture && !avatarError ? (
                <img
                  src={getMediaUrl(authorProfilePicture)}
                  alt={authorUsername}
                  onError={() => setAvatarError(true)}
                  className="w-11 h-11 rounded-full object-cover border border-zinc-200 dark:border-zinc-800 hover:opacity-90 transition-opacity"
                />
              ) : (
                <div className="w-11 h-11 rounded-full bg-zinc-200 dark:bg-zinc-800 flex items-center justify-center font-bold text-zinc-600 dark:text-zinc-300 text-sm">
                  {authorUsername.charAt(0).toUpperCase()}
                </div>
              )}
            </Link>
          ) : (
            <div className="flex-shrink-0">
              {authorProfilePicture && !avatarError ? (
                <img
                  src={getMediaUrl(authorProfilePicture)}
                  alt={authorUsername}
                  onError={() => setAvatarError(true)}
                  className="w-11 h-11 rounded-full object-cover border border-zinc-200 dark:border-zinc-800"
                />
              ) : (
                <div className="w-11 h-11 rounded-full bg-zinc-200 dark:bg-zinc-800 flex items-center justify-center font-bold text-zinc-600 dark:text-zinc-300 text-sm">
                  {authorUsername.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
          )}

          {/* Post Content Body */}
          <div className="flex-1 min-w-0">
            {/* Header row: Author info, timestamp, menu */}
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-x-2 gap-y-0.5 flex-wrap min-w-0">
                {authorHandle ? (
                  <Link
                    to={`/profile/${authorHandle}`}
                    onClick={handleAvatarClick}
                    className="font-bold text-zinc-900 dark:text-zinc-100 hover:underline text-[15.5px] sm:text-[16px] truncate"
                  >
                    <bdi>{authorUsername}</bdi>
                  </Link>
                ) : (
                  <span className="font-bold text-zinc-900 dark:text-zinc-100 text-[15.5px] sm:text-[16px] truncate">
                    <bdi>{authorUsername}</bdi>
                  </span>
                )}
                {authorIsVerified && (
                  <i className="fas fa-check-circle text-sky-500 text-[14px] shrink-0" title="Verified" />
                )}
                {post.author?.accountType === 'organization' && (
                  <span className="text-zinc-400 text-xs shrink-0" title="Organization">
                    <i className="fas fa-building" />
                  </span>
                )}
                {authorHandle && (
                  <Link
                    to={`/profile/${authorHandle}`}
                    onClick={handleAvatarClick}
                    className="text-zinc-500 dark:text-zinc-400 text-[14px] sm:text-[14.5px] font-normal truncate hover:underline"
                  >
                    <bdi dir="ltr">@{authorHandle}</bdi>
                  </Link>
                )}
                <span className="text-zinc-400 text-xs shrink-0 select-none">·</span>
                <span
                  className="text-zinc-400 dark:text-zinc-500 text-[13.5px] sm:text-[14px] font-normal shrink-0 hover:underline cursor-pointer"
                  title={new Date(post.createdAt).toLocaleString(i18n.language || undefined, {
                    dateStyle: 'full',
                    timeStyle: 'short',
                  })}
                >
                  <bdi>{formatTimeAgo(post.createdAt, i18n.language)}</bdi>
                </span>
                {post.editedAt && (
                  <span className="text-zinc-400 text-xs italic font-light shrink-0">
                    <bdi>({t('post.edited', 'edited')})</bdi>
                  </span>
                )}
                {isHidden && (
                  <span
                    className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 inline-flex items-center gap-1 shrink-0"
                    title={currentUser?.isAdmin ? 'Hidden from public (visible only to author)' : 'This post is visible only to you'}
                  >
                    <i className="fas fa-eye-slash text-[10px]" />
                    {currentUser?.isAdmin ? 'Author Only' : 'Only visible to you'}
                  </span>
                )}
              </div>

              {/* Post Options Menu */}
              {renderOptionsMenu()}
            </div>

            {/* Edit Box or Post Content */}
            {renderPostContent()}

            {/* Video Embed */}
            {videoInfo && <VideoEmbed provider={videoInfo.provider} videoId={videoInfo.videoId} />}

            {/* Link Preview */}
            {post.linkPreview && !videoInfo && (
              <div className="mb-2.5" onClick={(e) => e.stopPropagation()}>
                <LinkPreview preview={post.linkPreview} />
              </div>
            )}

            {/* Media Grid */}
            {renderMediaGrid()}

            {/* Voice Notes Audio Players */}
            {audioMedia.map((item, idx) => (
              <AudioPlayer key={idx} src={item.url} />
            ))}

            {/* Poll Component */}
            {renderPoll()}

            {/* Quoted Post Preview */}
            {renderQuotedPost()}

            {/* Tahaqqoq Community Context / Fact-Checking Note */}
            <TahaqqoqNoteCard postId={post.id} isPostAuthor={currentUser?.id === (post.author?.id || post.userId || post.user_id)} />

            {/* Action Row: Reply, Repost, Like, Bookmark, Share */}
            {renderActionRow()}

            {/* Desktop Inline Comments */}
            {showComments && showCommentsInline && !isMobile && (
              <div className="mt-3 pt-3 border-t border-zinc-100 dark:border-zinc-800/80" onClick={(e) => e.stopPropagation()}>
                <CommentsSection
                  postId={post.id}
                  postAuthorId={post.author?.id || post.userId}
                  onCommentAdded={() => {
                    if (onUpdate) onUpdate();
                    setShowComments(true);
                  }}
                  onCommentDeleted={onUpdate}
                  isMobile={false}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    )}

      {/* Fullscreen Edge-to-Edge Native Media Viewer */}
      {lightboxIndex !== null && imageVideoMedia.length > 0 && (
        <NativeImageViewer
          media={imageVideoMedia}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          post={post}
          liked={liked}
          likesCount={likesCount}
          onLike={handleLike}
          reBazzed={reBazzed}
          reBazzesCount={reBazzesCount}
          onReBazz={handleReBazz}
          repliesCount={post.repliesCount || 0}
          onReply={() => {
            if (isMobile && showCommentsInline) {
              openPostDetail();
            } else if (!isMobile) {
              setShowCommentsModal(true);
            } else {
              openPostDetail();
            }
          }}
          onShare={handleShareClick}
        />
      )}

      {/* Desktop Comments Modal */}
      {!isMobile && (
        <CommentsModal
          postId={post.id}
          isOpen={showCommentsModal}
          onClose={() => setShowCommentsModal(false)}
          onCommentAdded={() => {
            if (onUpdate) onUpdate();
            setShowCommentsModal(false);
          }}
        />
      )}

      {/* Dirham Tip Modal (gated by feature toggle) */}
      {isDirhamEnabled && (
        <DirhamTipModal
          isOpen={showTipModal}
          onClose={() => setShowTipModal(false)}
          recipient={post.author || {
            id: post.userId || post.user_id,
            username: post.username,
            full_name: post.full_name,
            profile_picture: post.profilePicture || post.profile_picture,
          }}
          postId={post.id}
        />
      )}
    </div>
  );
};

export default PostCard;
